import React, { useState, useEffect, useCallback } from 'react';
import {
  SignIn, SignOut, MagnifyingGlass, Clock, GearSix, QrCode,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import BarcodeScanner from '../../components/BarcodeScanner';
import { useAuth } from '../../context/AuthContext';
import gateAttendanceService from '../../services/gateAttendanceService';
import studentsService from '../../services/studentsService';
import classesService from '../../services/classesService';
import './security.css';
import './gate-attendance.css';

const TABS = [
  ['checkin', 'Check In / Out'],
  ['today', "Today's Log"],
  ['late-arrivals', 'Late Arrivals'],
  ['late-pickups', 'Late Pickups'],
];

const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('en-GH', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function GateAttendancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'school_admin';
  const [activeTab, setActiveTab] = useState('checkin');

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [actingOn, setActingOn] = useState(null); // student id currently being checked in/out
  const [biometricPinDrafts, setBiometricPinDrafts] = useState({}); // student_id -> pin being typed
  const [savingBiometricPinFor, setSavingBiometricPinFor] = useState(null);

  const [classes, setClasses] = useState([]);
  const [todayClassFilter, setTodayClassFilter] = useState('');
  const [today, setToday] = useState([]);
  const [loadingToday, setLoadingToday] = useState(false);

  const [summary, setSummary] = useState(null);

  const [lateArrivals, setLateArrivals] = useState([]);
  const [loadingLateArrivals, setLoadingLateArrivals] = useState(false);
  const [latePickups, setLatePickups] = useState([]);
  const [loadingLatePickups, setLoadingLatePickups] = useState(false);

  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ late_cutoff_time: '', pickup_late_cutoff_time: '', auto_mark_classroom_attendance: false });
  const [savingSettings, setSavingSettings] = useState(false);

  const [showScanPanel, setShowScanPanel] = useState(false);
  const [scanMode, setScanMode] = useState('check_in'); // 'check_in' | 'check_out'
  const [scanning, setScanning] = useState(true);
  const [scanBusy, setScanBusy] = useState(false);

  useEffect(() => {
    classesService.listClasses().then((data) => setClasses(data || [])).catch(() => setClasses([]));
  }, []);

  const loadSummary = useCallback(() => {
    gateAttendanceService.getDailySummary().then(setSummary).catch(() => setSummary(null));
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadToday = useCallback(async () => {
    setLoadingToday(true);
    try {
      const data = await gateAttendanceService.getToday(todayClassFilter || undefined);
      setToday(data || []);
    } catch (error) {
      toast.error('Failed to load today\'s gate log');
    } finally {
      setLoadingToday(false);
    }
  }, [todayClassFilter]);

  useEffect(() => {
    if (activeTab === 'today') loadToday();
  }, [activeTab, loadToday]);

  const loadLateArrivals = useCallback(async () => {
    setLoadingLateArrivals(true);
    try {
      setLateArrivals(await gateAttendanceService.getLateArrivals() || []);
    } catch (error) {
      toast.error('Failed to load late arrivals');
    } finally {
      setLoadingLateArrivals(false);
    }
  }, []);

  const loadLatePickups = useCallback(async () => {
    setLoadingLatePickups(true);
    try {
      setLatePickups(await gateAttendanceService.getLatePickups() || []);
    } catch (error) {
      toast.error('Failed to load late pickups');
    } finally {
      setLoadingLatePickups(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'late-arrivals') loadLateArrivals();
    if (activeTab === 'late-pickups') loadLatePickups();
  }, [activeTab, loadLateArrivals, loadLatePickups]);

  // Debounced student search for the check-in/out tab
  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      studentsService.listStudents({ search: search.trim(), limit: 10 })
        .then((data) => setSearchResults(data?.items || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const doCheckIn = async (student) => {
    setActingOn(student.id);
    try {
      const result = await gateAttendanceService.checkIn(student.id);
      toast.success(`${student.first_name || student.full_name} checked in${result.is_late ? ' (late)' : ''}`);
      loadSummary();
      if (activeTab === 'today') loadToday();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record check-in');
    } finally {
      setActingOn(null);
    }
  };

  const doCheckOut = async (student) => {
    setActingOn(student.id);
    try {
      const result = await gateAttendanceService.checkOut(student.id);
      toast.success(`${student.first_name || student.full_name} checked out${result.is_late_pickup ? ' (late pickup)' : ''}`);
      loadSummary();
      if (activeTab === 'today') loadToday();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record check-out');
    } finally {
      setActingOn(null);
    }
  };

  const handleCardScan = async (cardNumber) => {
    setScanning(false);
    setScanBusy(true);
    try {
      const result = scanMode === 'check_in'
        ? await gateAttendanceService.checkInByScan(cardNumber.trim())
        : await gateAttendanceService.checkOutByScan(cardNumber.trim());
      toast.success(result.message || 'Recorded');
      loadSummary();
      if (activeTab === 'today') loadToday();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not read that card');
    } finally {
      setScanBusy(false);
      setScanning(true); // ready for the next student in the queue
    }
  };

  const handleSaveBiometricPin = async (studentId) => {
    const pin = (biometricPinDrafts[studentId] || '').trim();
    if (!pin) return;
    try {
      setSavingBiometricPinFor(studentId);
      await gateAttendanceService.setBiometricPin(studentId, pin);
      toast.success('Biometric PIN saved');
      setBiometricPinDrafts((prev) => ({ ...prev, [studentId]: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to save biometric PIN');
    } finally {
      setSavingBiometricPinFor(null);
    }
  };

  const openSettings = async () => {
    try {
      const data = await gateAttendanceService.getSettings();
      setSettings({
        late_cutoff_time: data.late_cutoff_time || '',
        pickup_late_cutoff_time: data.pickup_late_cutoff_time || '',
        auto_mark_classroom_attendance: !!data.auto_mark_classroom_attendance,
      });
      setShowSettings(true);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await gateAttendanceService.updateSettings(settings);
      toast.success('Gate attendance settings saved');
      setShowSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const pageContent = (
    <div className="sec-page">
      <div className="sec-header">
        <div className="sec-header-left">
          <div className="sec-shield-icon"><SignIn size={22} weight="fill" /></div>
          <div>
            <div className="sec-header-title">Gate Attendance</div>
            <div className="sec-header-sub">Record student arrivals and departures at the entrance</div>
          </div>
        </div>
        {isAdmin && (
          <button className="ga-settings-btn" onClick={openSettings} title="Configure lateness cutoff times">
            <GearSix size={18} /> Settings
          </button>
        )}
      </div>

      {summary && (
        <div className="sec-stats-bar">
          <div className="sec-stat">
            <div className="sec-stat-value blue">{summary.checked_in}/{summary.total_students}</div>
            <div className="sec-stat-label">Checked In Today</div>
          </div>
          <div className="sec-stat">
            <div className="sec-stat-value amber">{summary.late_arrivals}</div>
            <div className="sec-stat-label">Late Arrivals</div>
          </div>
          <div className="sec-stat">
            <div className="sec-stat-value green">{summary.checked_out}</div>
            <div className="sec-stat-label">Checked Out</div>
          </div>
          <div className="sec-stat">
            <div className="sec-stat-value red">{summary.late_pickups}</div>
            <div className="sec-stat-label">Late Pickups</div>
          </div>
        </div>
      )}

      <div className="sec-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} className={`sec-tab ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'checkin' && (
        <div className="ga-checkin-panel">
          <div className="ga-search-box">
            <MagnifyingGlass size={18} />
            <input
              type="text"
              placeholder="Search student by name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <button
              type="button"
              className="ga-scan-toggle-btn"
              onClick={() => setShowScanPanel((v) => !v)}
            >
              <QrCode size={18} weight="bold" /> {showScanPanel ? 'Hide Scanner' : 'Scan ID Card'}
            </button>
          </div>

          {showScanPanel && (
            <div className="ga-scan-panel">
              <div className="ga-scan-mode-toggle">
                <button
                  type="button"
                  className={`ga-scan-mode-btn ${scanMode === 'check_in' ? 'active' : ''}`}
                  onClick={() => setScanMode('check_in')}
                >
                  <SignIn size={16} weight="bold" /> Check In
                </button>
                <button
                  type="button"
                  className={`ga-scan-mode-btn ${scanMode === 'check_out' ? 'active' : ''}`}
                  onClick={() => setScanMode('check_out')}
                >
                  <SignOut size={16} weight="bold" /> Check Out
                </button>
              </div>
              <BarcodeScanner active={showScanPanel && scanning} onScan={handleCardScan} manualLabel="Enter card number manually" />
              {scanBusy && <p className="ga-hint">Recording…</p>}
            </div>
          )}

          {searching && <p className="ga-hint">Searching...</p>}
          <div className="ga-search-results">
            {searchResults.map((s) => (
              <div key={s.id} className="ga-search-row">
                <div className="ga-search-row-info">
                  <span className="ga-search-row-name">{s.first_name} {s.last_name}</span>
                  <span className="ga-search-row-meta">{s.student_id} · {s.class_name || 'No class'}</span>
                </div>
                <div className="ga-search-row-actions">
                  <button
                    className="ga-action-btn checkin"
                    disabled={actingOn === s.id}
                    onClick={() => doCheckIn(s)}
                  >
                    <SignIn size={16} weight="bold" /> Check In
                  </button>
                  <button
                    className="ga-action-btn checkout"
                    disabled={actingOn === s.id}
                    onClick={() => doCheckOut(s)}
                  >
                    <SignOut size={16} weight="bold" /> Check Out
                  </button>
                </div>
                <div className="ga-search-row-pin">
                  <input
                    type="text"
                    placeholder="PIN from device enrollment"
                    value={biometricPinDrafts[s.id] || ''}
                    onChange={(e) => setBiometricPinDrafts((prev) => ({ ...prev, [s.id]: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="ga-action-btn neutral"
                    disabled={savingBiometricPinFor === s.id || !(biometricPinDrafts[s.id] || '').trim()}
                    onClick={() => handleSaveBiometricPin(s.id)}
                  >
                    {savingBiometricPinFor === s.id ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ))}
            {!searching && search.trim() && searchResults.length === 0 && (
              <p className="ga-hint">No students found matching "{search}"</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'today' && (
        <div>
          <div className="ga-filter-row">
            <select className="ga-select" value={todayClassFilter} onChange={(e) => setTodayClassFilter(e.target.value)}>
              <option value="">All Classes</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {loadingToday ? (
            <p className="ga-hint">Loading...</p>
          ) : (
            <table className="ga-table">
              <thead>
                <tr><th>Student</th><th>Check In</th><th>Check Out</th><th>Status</th></tr>
              </thead>
              <tbody>
                {today.map((r) => (
                  <tr key={r.student_id}>
                    <td>{r.student_name}</td>
                    <td>{fmtTime(r.check_in_time)}</td>
                    <td>{fmtTime(r.check_out_time)}</td>
                    <td>
                      {!r.check_in_time && <span className="sec-status-badge badge-pending">Not Arrived</span>}
                      {r.check_in_time && r.is_late && <span className="sec-status-badge badge-pending">Late Arrival</span>}
                      {r.check_in_time && !r.is_late && !r.check_out_time && <span className="sec-status-badge badge-verified">On Campus</span>}
                      {r.check_out_time && r.is_late_pickup && <span className="sec-status-badge badge-pending">Late Pickup</span>}
                      {r.check_out_time && !r.is_late_pickup && <span className="sec-status-badge badge-picked_up">Departed</span>}
                    </td>
                  </tr>
                ))}
                {today.length === 0 && <tr><td colSpan={4} className="ga-empty">No students found</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'late-arrivals' && (
        <div>
          {loadingLateArrivals ? (
            <p className="ga-hint">Loading...</p>
          ) : (
            <table className="ga-table">
              <thead><tr><th>Student</th><th>Date</th><th>Check-In Time</th></tr></thead>
              <tbody>
                {lateArrivals.map((r) => (
                  <tr key={r.id}>
                    <td>{r.student_name}</td>
                    <td>{r.date}</td>
                    <td>{fmtDateTime(r.check_in_time)}</td>
                  </tr>
                ))}
                {lateArrivals.length === 0 && <tr><td colSpan={3} className="ga-empty">No late arrivals in the last 30 days</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'late-pickups' && (
        <div>
          {loadingLatePickups ? (
            <p className="ga-hint">Loading...</p>
          ) : (
            <table className="ga-table">
              <thead><tr><th>Student</th><th>Date</th><th>Check-Out Time</th></tr></thead>
              <tbody>
                {latePickups.map((r) => (
                  <tr key={r.id}>
                    <td>{r.student_name}</td>
                    <td>{r.date}</td>
                    <td>{fmtDateTime(r.check_out_time)}</td>
                  </tr>
                ))}
                {latePickups.length === 0 && <tr><td colSpan={3} className="ga-empty">No late pickups in the last 30 days</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showSettings && (
        <div className="ga-modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="ga-modal" onClick={(e) => e.stopPropagation()}>
            <h3><Clock size={18} /> Lateness Cutoff Times</h3>
            <p className="ga-hint">Leave a field blank to disable lateness detection for that side.</p>
            <label>
              Gate check-in cutoff (school start time)
              <input
                type="time"
                value={settings.late_cutoff_time}
                onChange={(e) => setSettings({ ...settings, late_cutoff_time: e.target.value })}
              />
            </label>
            <label>
              Pickup cutoff (dismissal time)
              <input
                type="time"
                value={settings.pickup_late_cutoff_time}
                onChange={(e) => setSettings({ ...settings, pickup_late_cutoff_time: e.target.value })}
              />
            </label>
            <label className="ga-checkbox-label">
              <input
                type="checkbox"
                checked={settings.auto_mark_classroom_attendance}
                onChange={(e) => setSettings({ ...settings, auto_mark_classroom_attendance: e.target.checked })}
              />
              Also mark classroom attendance as Present on check-in
            </label>
            <p className="ga-hint">
              Only fills in today's attendance if a teacher hasn't already marked it — a teacher's own mark always wins.
            </p>
            <div className="ga-modal-actions">
              <button className="ga-btn-secondary" onClick={() => setShowSettings(false)}>Cancel</button>
              <button className="ga-btn-primary" disabled={savingSettings} onClick={saveSettings}>
                {savingSettings ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return <Layout>{pageContent}</Layout>;
}
